import { useNavigate } from '@tanstack/react-router';
import { ChevronsUpDown, LogOut, Monitor, Moon, Sun, User } from 'lucide-react';

import { signOut, useSession } from '@/shared/auth';
import { cx } from '@/shared/lib/cx';
import { useTheme, type ThemeMode } from '@/shared/lib/useTheme';
import { t } from '@/shared/i18n';
import { Avatar } from '@/shared/ui/Avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/DropdownMenu';
import { toast } from '@/shared/ui/Toast';

import styles from './UserMenu.module.scss';

/**
 * Меню пользователя внизу боковой колонки: профиль, тема, выход.
 *
 * Тема — здесь, а не отдельной кнопкой в шапке: у неё три состояния, и
 * переключатель-кнопка умеет показать только два.
 */
export function UserMenu({ compact = false }: { compact?: boolean }) {
  const session = useSession();
  const navigate = useNavigate();
  const { mode, setMode } = useTheme();

  if (session.status !== 'authenticated') {
    return null;
  }

  const { user } = session;
  const name = user.name === '' ? user.email : user.name;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cx(styles['trigger'], compact && styles['trigger--compact'])}
          aria-label={compact ? name : undefined}
        >
          <Avatar name={name} size="sm" />
          {!compact && (
            <>
              <span className={styles['trigger__text']}>
                <span className={styles['trigger__name']}>{name}</span>
                <span className={styles['trigger__email']}>{user.email}</span>
              </span>
              <ChevronsUpDown className={styles['trigger__chevron']} />
            </>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" side="top" sideOffset={8}>
        <DropdownMenuItem
          icon={<User />}
          onSelect={() => {
            void navigate({ to: '/app/profile' });
          }}
        >
          {t.userMenu.profile}
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t.userMenu.theme}</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={mode}
          onValueChange={(value) => {
            setMode(value as ThemeMode);
          }}
        >
          <DropdownMenuRadioItem value="system" icon={<Monitor />}>
            {t.userMenu.themeSystem}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark" icon={<Moon />}>
            {t.userMenu.themeDark}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="light" icon={<Sun />}>
            {t.userMenu.themeLight}
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>

        <DropdownMenuSeparator />
        <DropdownMenuItem
          icon={<LogOut />}
          onSelect={() => {
            signOut()
              .catch(() => {
                toast.error(t.common.networkError);
              })
              .finally(() => {
                void navigate({ to: '/login' });
              });
          }}
        >
          {t.userMenu.signOut}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
